import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import debounce from 'lodash/debounce'
import { Swiper, SwiperSlide } from 'swiper/react'
import {
  Pagination,
  Autoplay,
  EffectFade,
  Navigation,
} from 'swiper'
import styles from './styles'
import { API_IMAGE_OBJECT } from '../../utils/types'
import 'swiper/swiper-bundle.min.css'
import HeroCard from '../HeroCard'
import getEnv from '../../utils/env'
import PreviewPlayerUtils from '../../utils/previewPlayer'

const HeroScroller = ({
  assets,
  recoID,
  liveProgramme,
}) => {
  const playerUtils = PreviewPlayerUtils()
  const [showCTA, setShowCTA] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const [loadPlayer, setLoadPlayer] = useState(getEnv() !== 'testing')
  const { swiperRef } = playerUtils

  const minSwipeDistance = 150

  const onTouchMove = (e) => {
    if (Math.abs(e.touches.diff) >= minSwipeDistance) {
      setShowCTA(false)
    }
  }

  useEffect(() => {
    updateLayout()
    playerUtils.checkVisible()
    window.addEventListener('scroll', playerUtils.checkVisible)
    window.addEventListener('resize', debounce(updateLayout, 300))
    window.addEventListener('message', playerUtils.handleMessage)
    return () => {
      window.removeEventListener('message', playerUtils.handleMessage)
    }
  }, [])

  const updateLayout = () => {
    if (window.innerWidth >= 768) {
      setIsMobile(false)
    } else {
      setIsMobile(true)
    }
  }

  const slideTransitionStartHandler = () => {
    if (swiperRef.current.swiper === null) return
    const { activeIndex } = swiperRef.current.swiper
    const slidesLength = swiperRef.current.swiper.slides.length

    if (activeIndex === slidesLength - 1) {
      setLoadPlayer(false)
    }

    setShowCTA(false)
    if (!swiperRef.current.swiper.autoplay.running) swiperRef.current.swiper.autoplay.start()
  }

  const swiperProps = {
    spaceBetween: 0,
    slidesPerView: 1,
    onSlideChangeTransitionStart: () => { slideTransitionStartHandler() },
    onSlideChange: () => { playerUtils.setPreviewPlaying(false) },
    onTransitionEnd: () => { setShowCTA(true) },
    onTouchMove: (e) => { onTouchMove(e) },
    updateOnWindowResize: true,
    loop: true,
    pagination: {
      clickable: true,
      type: 'bullets',
    },
    effect: 'fade',
    observer: true,
    modules: [Pagination, Autoplay, EffectFade, Navigation],
    autoplay: {
      delay: 6000,
      disableOnInteraction: false,
    },
    speed: 1000,
    watchSlidesProgress: true,
    shortSwipes: false,
    navigation: true,
  }

  const mobileSwiperProps = {
    ...swiperProps,
    effect: 'slide',
    autoplay: false,
    speed: 300,
    shortSwipes: true,
  }

  return (
    <div
      className="hero-container"
    >
      {!isMobile && (
        <Swiper {...swiperProps} ref={swiperRef} watchSlidesProgress>
          {assets?.map((asset, i) => (
            <SwiperSlide key={asset.id}>
              {({ isVisible }) => (
                <>
                  <HeroCard
                    playing={playerUtils.previewPlaying}
                    showCTA={showCTA}
                    key={asset.id}
                    specialEvent
                    noScale
                    recoID={recoID}
                    {...asset}
                    showTitle={false}
                    index={i}
                    description={asset.description}
                    isVisible={isVisible}
                    inViewScroll={playerUtils.inView}
                    userPaused={playerUtils.previewUserPause}
                    firstRotation={loadPlayer}
                    isSubtitle={asset.isSubtitle}
                    isGuidance={asset.isGuidance}
                    prgmGenre={asset.genre}
                    subGenre={asset.subGenre}
                    audioDescribed={asset.audioDescription}
                    availableEpisodes={asset.availableEpisodes}
                    liveProgramme={liveProgramme}
                  />
                </>
              )}
            </SwiperSlide>
          ))}
        </Swiper>

      )}
      {isMobile && (
        <Swiper {...mobileSwiperProps}>
          {assets?.map((asset, i) => (
            <SwiperSlide key={asset.id}>
              <HeroCard
                showCTA={showCTA}
                key={asset.id}
                specialEvent
                noScale
                recoID={recoID}
                {...asset}
                showTitle={false}
                index={i}
                prgmGenre={asset.genre}
                subGenre={asset.subGenre}
                isSubtitle={asset.isSubtitle}
                isGuidance={asset.isGuidance}
                audioDescribed={asset.audioDescription}
                description={asset.description}
                liveProgramme={liveProgramme}
                isVisible
              />
            </SwiperSlide>
          ))}
        </Swiper>
      )}
      <style jsx>{styles}</style>
    </div>
  )
}

HeroScroller.propTypes = {
  assets: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      link: PropTypes.string,
      image: API_IMAGE_OBJECT,
      posters: PropTypes.array,
      subtitle: PropTypes.string,
      label: PropTypes.string,
      overlay: PropTypes.string,
      isSubtitle: PropTypes.bool,
      isGuidance: PropTypes.bool,
      audioDescription: PropTypes.bool,
      genre: PropTypes.string,
      availableEpisodes: PropTypes.string,
    })
  ),
  recoID: PropTypes.string,
  liveProgramme: PropTypes.shape({
    title: PropTypes.string,
    guid: PropTypes.string,
  }),
}

HeroScroller.defaultProps = {
  recoID: null,
  assets: [],
  liveProgramme: {},
}

export default HeroScroller
